import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import CustomInput from "../CustomInput";
import CustomSelect from "../CustomSelect";
import CustomDatePicker from "../CustomDatePicker";
import CustomPhoneNumberInput from "../CustomPhoneNumberInput";

/**
 * Single Field component for every data-entry surface in the app. Dispatches
 * to the existing primitives (CustomInput / CustomSelect / etc.) based on
 * `type`, and wires errors through react-hook-form so every field surfaces
 * validation the same way (red border + helper text under the field).
 *
 * Supported types:
 *   text (default), email, password, number, tel, url, textarea
 *   select       — options: [{ label, value }]; searchable={true} for combobox
 *   multiselect  — same options shape; uses react-select with isMulti
 *   date         — react-datepicker; value is a JS Date
 *   phone        — react-phone-number-input; value is E.164 string
 *   checkbox     — boolean
 *   radio        — options: [{ label, value }]; renders horizontal radio group
 *
 * `name` accepts dot-paths (e.g. "address.city") and resolves via RHF's get().
 */
const getByPath = (obj, path) =>
  path.split(".").reduce((o, k) => (o == null ? o : o[k]), obj);

const Field = ({
  name,
  label,
  type = "text",
  options,
  required = false,
  placeholder,
  className = "",
  searchable = false,
  showInfo,
  infoText,
  endAdornment,
  ...rest
}) => {
  const { register, control, formState: { errors } } = useFormContext();
  const errorMsg = getByPath(errors, name)?.message;

  // Plain inputs (text / number / textarea / etc.) wire directly via register —
  // cheaper than Controller, and CustomInput already accepts ref + error prop.
  if (["text", "email", "password", "number", "tel", "url", "textarea"].includes(type)) {
    return (
      <CustomInput
        label={label}
        required={required}
        textarea={type === "textarea"}
        type={type === "textarea" ? undefined : type}
        error={errorMsg}
        showInfo={showInfo}
        infoText={infoText}
        endAdornment={endAdornment}
        className={className}
        placeholder={placeholder}
        {...register(name)}
        {...rest}
      />
    );
  }

  // Everything else needs Controller because the underlying components are
  // controlled (value/onChange) and don't speak the native ref dance.
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        if (type === "select" || type === "multiselect") {
          const isMulti = type === "multiselect";
          // CustomSelect expects an option object (or array of them) for
          // react-select mode. We accept primitive values in form state and
          // convert here, so consumers don't have to think about it.
          const valueObj = isMulti
            ? options?.filter((o) => Array.isArray(field.value) && field.value.includes(o.value))
            : options?.find((o) => o.value === field.value) || null;
          // Force react-select mode (CustomSelect uses react-select when
          // `styles` is truthy OR isSearchable is true). Pass an empty styles
          // object to opt in to the styled flamingo dropdown unconditionally
          // — this is the "every dropdown looks the same" guarantee.
          //
          // Dropdown-positioning trifecta:
          //   menuPortalTarget=document.body  — escape the modal's
          //     overflow-auto clipping so long option lists are never cut off
          //   menuPlacement="auto"            — prefer to render below the
          //     trigger but flip upward if there isn't room (short lists
          //     stay visually inside the modal that way)
          //   closeMenuOnScroll=<fn>          — close the menu if the modal
          //     body scrolls; otherwise the fixed-position menu and the
          //     trigger visually disconnect
          return (
            <div className={className}>
              <CustomSelect
                label={label}
                required={required}
                placeholder={placeholder}
                options={options}
                isSearchable={searchable ?? false}
                isMulti={isMulti}
                styles={{}}
                // `classNamePrefix` makes react-select emit stable class names
                // (select__menu, select__option, ...) instead of emotion's
                // generated ones — so the closeMenuOnScroll matcher below can
                // reliably tell "scroll inside menu" from "scroll outside menu".
                classNamePrefix="select"
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPosition="fixed"
                menuPlacement="auto"
                closeMenuOnScroll={(e) => {
                  // Only close when an *ancestor* of the menu scrolls — not
                  // when the user scrolls inside the menu's own option list.
                  // Without the classNamePrefix above, .select__menu would
                  // never match and the menu would close on every wheel event.
                  if (!(e?.target instanceof Element)) return false;
                  return !e.target.closest?.(".select__menu");
                }}
                value={valueObj}
                onChange={(opt) =>
                  field.onChange(isMulti ? (opt || []).map((o) => o.value) : opt?.value ?? "")
                }
                onBlur={field.onBlur}
                size="form"
                {...rest}
              />
              {errorMsg && <p className="mt-1 text-xs text-red-500">{errorMsg}</p>}
            </div>
          );
        }

        if (type === "date") {
          return (
            <div className={className}>
              <CustomDatePicker
                label={label}
                required={required}
                selected={field.value || null}
                onChange={field.onChange}
                placeholderText={placeholder}
                {...rest}
              />
              {errorMsg && <p className="mt-1 text-xs text-red-500">{errorMsg}</p>}
            </div>
          );
        }

        if (type === "phone") {
          return (
            <CustomPhoneNumberInput
              label={label}
              required={required}
              value={field.value || ""}
              onChange={field.onChange}
              error={errorMsg}
              className={className}
              placeholder={placeholder}
              {...rest}
            />
          );
        }

        if (type === "checkbox") {
          return (
            <div className={className}>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
                <input
                  type="checkbox"
                  checked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  onBlur={field.onBlur}
                  className="rounded border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
                />
                {label}
                {required && <span className="text-red-500">*</span>}
              </label>
              {errorMsg && <p className="mt-1 text-xs text-red-500">{errorMsg}</p>}
            </div>
          );
        }

        if (type === "radio") {
          return (
            <div className={className}>
              {label && (
                <label className="flex text-sm font-normal text-slate-500 mb-1 items-center gap-1">
                  {label}
                  {required && <span className="text-red-500 text-xl">*</span>}
                </label>
              )}
              <div className="flex flex-wrap gap-4">
                {(options || []).map((opt) => (
                  <label key={opt.value} className="inline-flex items-center gap-2 text-sm text-slate-700 select-none">
                    <input
                      type="radio"
                      checked={field.value === opt.value}
                      onChange={() => field.onChange(opt.value)}
                      onBlur={field.onBlur}
                      className="border-slate-300 text-flamingo-500 focus:ring-flamingo-500"
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
              {errorMsg && <p className="mt-1 text-xs text-red-500">{errorMsg}</p>}
            </div>
          );
        }

        // Unknown type — render as text and emit a console warning during dev.
        // eslint-disable-next-line no-console
        console.warn(`[Field] Unknown type "${type}" for name "${name}", falling back to text.`);
        return (
          <CustomInput
            label={label}
            required={required}
            error={errorMsg}
            className={className}
            placeholder={placeholder}
            value={field.value ?? ""}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
            {...rest}
          />
        );
      }}
    />
  );
};

export default Field;
